import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import i18n from '../i18n';
import toast from 'react-hot-toast';

let API_BASE_URL: string;

API_BASE_URL = `${window.location.origin}`;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    const preferredLang = sessionStorage.getItem('lang') || localStorage.getItem('lang') || navigator.language;

    // Ensure headers object exists
    if (!config.headers) {
      config.headers = {} as any;
    }

    // Always attach Authorization if token exists
    if (token) {
      (config.headers as any)['Authorization'] = `Bearer ${token}`;
    }


    // Attach Accept-Language header for backend i18n
    if (preferredLang) {
      (config.headers as any)['Accept-Language'] = preferredLang;
    }

    // Debug logging for user creation requests (v1 and v2)
    if ((config.url?.includes('/api/v1/users/') || config.url?.includes('/api/v2/users')) && config.method === 'post') {
      console.log('API Request Debug:', {
        url: config.url,
        method: config.method,
        headers: config.headers,
        data: config.data,
      });
    }

    // Debug logging for asset deletion requests (v1 and v2)
    if (config.method === 'delete' && (config.url?.includes('/api/v1/assets/') || config.url?.includes('/api/v2/assets/'))) {
      console.log('API DELETE Debug:', {
        url: config.url,
        method: config.method,
        authHeader: (config.headers as any)['Authorization'],
      });
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    const method = (error.config?.method || '').toUpperCase();
    const token = localStorage.getItem('token');

    // Avoid redirect/toast for auth endpoints (login, 2FA, session check) — let callers handle these
    const isAuthEndpoint =
      url.includes('/api/v1/auth/login') ||
      url.includes('/api/v1/auth/2fa') ||
      url.includes('/api/v2/auth/login') ||
      url.includes('/api/v2/auth/2fa') ||
      url.includes('/api/v2/auth/me') ||
      url.includes('/auth/login') ||
      url.includes('/auth/2fa') ||
      url.includes('/auth/me');

    // For auth endpoints, let the caller (AuthContext) handle the response
    if (isAuthEndpoint) {
      return Promise.reject(error);
    }

    if (status === 401) {
      if (token) {
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
        toast.error(i18n.t('Session expired. Please log in again.'));
        // Do not use window.location.href — let React state handle navigation
        // AuthContext.refreshUser() will detect user === null and redirect via Router
        window.dispatchEvent(new CustomEvent('auth:logout'));
      }
    } else if (status === 403) {
      toast.error(i18n.t('Access denied. You do not have permission for this action.'));
    } else if ((status || 0) >= 500) {
      toast.error(i18n.t('Internal server error. Try again later.'));
    } else if (error.code === 'ECONNABORTED') {
      toast.error(i18n.t('Request timeout. Check your connection.'));
    }

    return Promise.reject(error);
  },
);

export default api;
